
import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Clock, FolderTree, Users, AlertTriangle, LayoutDashboard } from "lucide-react";
import { ImportResult } from "@/services/imports/importTypes";
import PendingPromptsTab from "@/components/admin/PendingPromptsTab";
import CategoryManager from "@/components/admin/CategoryManager";
import UsersTab from "@/components/admin/UsersTab";
import ReportsTab from "@/components/admin/ReportsTab";
import AdminDashboard from "@/components/admin/AdminDashboard";

interface AdminTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  pendingPrompts: any[];
  isLoadingPrompts: boolean;
  handleApprove: (id: string) => Promise<void>;
  handleReject: (id: string) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
  users: any[];
  isLoadingUsers: boolean;
  fetchUsers: () => Promise<void>;
  totalCategories: number;
  reportedIssues: number;
}

const AdminTabs: React.FC<AdminTabsProps> = ({
  activeTab,
  setActiveTab,
  pendingPrompts,
  isLoadingPrompts,
  handleApprove,
  handleReject,
  handleDelete,
  users,
  isLoadingUsers,
  fetchUsers,
  totalCategories,
  reportedIssues,
}) => {
  return (
    <Tabs 
      defaultValue="dashboard" 
      value={activeTab} 
      onValueChange={setActiveTab}
      className="w-full"
    >
      <div className="px-6 border-b border-border/40">
        <TabsList className="justify-start bg-transparent h-14 p-0 gap-8">
          <TabsTrigger 
            value="dashboard"
            className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
          >
            <LayoutDashboard className="h-4 w-4" />
            <span>Dashboard</span>
          </TabsTrigger>
          
          <TabsTrigger 
            value="pending"
            className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
          >
            <Clock className="h-4 w-4" />
            <span>Pending Approval</span>
            <span className="ml-1 bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs">
              {pendingPrompts.length}
            </span>
          </TabsTrigger>
          
          <TabsTrigger 
            value="categories"
            className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
          >
            <FolderTree className="h-4 w-4" />
            <span>Categories</span>
          </TabsTrigger>
          
          <TabsTrigger 
            value="users"
            className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
          >
            <Users className="h-4 w-4" />
            <span>Users</span>
          </TabsTrigger>
          
          <TabsTrigger 
            value="reports"
            className="flex items-center gap-2 h-full rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-0 py-0"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>Reports</span>
          </TabsTrigger>
        </TabsList>
      </div>
      
      <div className="p-6">
        <TabsContent value="dashboard" className="mt-0">
          <AdminDashboard 
            pendingPrompts={pendingPrompts.length}
            totalUsers={users.length}
            totalCategories={totalCategories}
            reportedIssues={reportedIssues}
          />
        </TabsContent>
        
        <TabsContent value="pending" className="mt-0 space-y-6">
          <h2 className="text-xl font-semibold mb-4">Prompts Pending Approval</h2>
          
          <PendingPromptsTab 
            pendingPrompts={pendingPrompts}
            isLoading={isLoadingPrompts}
            handleApprove={handleApprove}
            handleReject={handleReject}
            handleDelete={handleDelete}
          />
        </TabsContent>
        
        <TabsContent value="categories" className="mt-0">
          <CategoryManager />
        </TabsContent>
        
        <TabsContent value="users" className="mt-0">
          <h2 className="text-xl font-semibold mb-4">Manage Users</h2>
          
          <UsersTab 
            users={users}
            isLoadingUsers={isLoadingUsers}
            fetchUsers={async () => {
              await fetchUsers();
            }}
          />
        </TabsContent>
        
        <TabsContent value="reports" className="mt-0">
          <ReportsTab />
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default AdminTabs;
